import { Box, HStack } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { useEffect, useState } from "react";

const characters = [
  // Empty character (using non-breaking space to maintain height)
  "\u00A0",
  // Uppercase letters
  ..."ABCDEFGHIJKLMNOPQRSTUVWXYZ",
  // Numbers
  ..."0123456789",
  // Special characters
  ..." .,!?@#$%^&*()-_+=[]{}|:;\"'<>/\\",
].join("");

interface SplitFlapDisplayProps {
  text: string;
  duration?: number;
  charDelay?: number;
  fontSize?: string | Record<string, string>;
}

const flipAnimation = keyframes`
  0% {
    transform: rotateX(0deg) translateZ(0);
  }
  15% {
    transform: rotateX(-30deg) translateZ(20px);
  }
  50% {
    transform: rotateX(-90deg) translateZ(40px);
  }
  50.1% {
    transform: rotateX(90deg) translateZ(40px);
  }
  85% {
    transform: rotateX(30deg) translateZ(20px);
  }
  100% {
    transform: rotateX(0deg) translateZ(0);
  }
`;

const SplitFlapChar = ({
  targetChar = " ",
  duration = 100,
  startDelay = 0,
  fontSize = { base: "2xl", md: "4xl" },
}) => {
  const [currentChar, setCurrentChar] = useState("\u00A0");
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldStart, setShouldStart] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShouldStart(true);
    }, startDelay);

    return () => clearTimeout(timer);
  }, [startDelay, targetChar]);

  useEffect(() => {
    if (!shouldStart || currentChar === targetChar) return;

    setIsAnimating(true);
    let currentIndex = characters.indexOf(currentChar);
    const targetIndex = characters.indexOf(
      targetChar === " " ? "\u00A0" : targetChar
    );

    // If character isn't in our set, just show it immediately
    if (targetIndex === -1) {
      setCurrentChar(targetChar);
      setIsAnimating(false);
      return;
    }

    // Calculate shortest path to target
    let distance = targetIndex - currentIndex;
    const totalChars = characters.length;

    if (Math.abs(distance) > totalChars / 2) {
      distance = distance > 0 ? distance - totalChars : distance + totalChars;
    }

    const interval = setInterval(() => {
      currentIndex =
        (currentIndex + Math.sign(distance) + totalChars) % totalChars;
      setCurrentChar(characters[currentIndex]);

      if (currentIndex === targetIndex) {
        clearInterval(interval);
        setIsAnimating(false);
      }
    }, duration / totalChars);

    return () => clearInterval(interval);
  }, [targetChar, duration, shouldStart]);

  return (
    <Box
      bg="black"
      color="white"
      px={2}
      py={2}
      borderRadius="md"
      fontSize={fontSize}
      fontFamily="mono"
      fontWeight="bold"
      position="relative"
      minW="1.2em"
      height="1.6em"
      display="flex"
      alignItems="center"
      justifyContent="center"
      _after={{
        content: '""',
        position: "absolute",
        left: 0,
        right: 0,
        top: "50%",
        height: "1px",
        backgroundColor: "rgba(255,255,255,0.3)",
        transform: "translateY(-50%)",
        boxShadow: "0 0 2px rgba(255,255,255,0.2)",
      }}
      style={{
        animation: isAnimating
          ? `${flipAnimation} ${duration / characters.length}ms infinite linear`
          : undefined,
        perspective: "400px",
        transformStyle: "preserve-3d",
        boxShadow: isAnimating
          ? "0 8px 16px rgba(0,0,0,0.5)"
          : "0 4px 8px rgba(0,0,0,0.3)",
        transform: `translateZ(${isAnimating ? "20px" : "0"})`,
        backfaceVisibility: "hidden",
        transition: "all 0.3s ease-in-out",
      }}
      _before={{
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(0,0,0,0.1) 100%)",
        pointerEvents: "none",
        borderRadius: "md",
      }}
    >
      {currentChar}
    </Box>
  );
};

export const SplitFlapDisplay = ({
  text,
  duration = 5000,
  charDelay = 100,
}: SplitFlapDisplayProps) => {
  const [displayText, setDisplayText] = useState(text);
  const [prevText, setPrevText] = useState(text);

  useEffect(() => {
    // Handle text length changes
    const maxLength = Math.max(text.length, prevText.length);
    const paddedNew = text.padEnd(maxLength, " ");

    setPrevText(paddedNew);
    setDisplayText(paddedNew);
  }, [text]);

  return (
    <HStack gap={1} justify="left" wrap="wrap">
      {displayText
        .toUpperCase()
        .split("")
        .map((char, index) => (
          <SplitFlapChar
            key={`${index}-${prevText}`}
            targetChar={char}
            duration={duration}
            startDelay={index * charDelay}
            fontSize={{ base: "sm", md: "lg" }}
          />
        ))}
    </HStack>
  );
};
